import { useState, useRef, useEffect } from "react";
import { Archive, ArchiveRestore, Palette, Wrench, Heart, Trash2, Pin, Pencil, Clapperboard, BookOpen, FolderInput, FolderMinus, ChevronRight, AlarmClock, Sofa } from "lucide-react";
import type { Project, Session } from "../auth/types";
import { useI18n } from "../i18n";

interface SessionItemProps {
  session: Session;
  active: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: (label: string) => void;
  onTogglePin: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  archived?: boolean;
  projects?: Project[];
  onMoveToProject?: (project: Project | null) => void;
  projectMovePending?: boolean;
  /** Session-bound wakes, slice 2′ — "Wake me on this" opens the Schedules
   *  panel prefilled with this session as the wake's origin. Absent for
   *  callers that don't offer scheduling; the menu item renders only when
   *  both this prop is present and the row is not archived. */
  onWakeMeOnThis?: () => void;
  /** Companion display name for this session (e.g. "Rook" / "Rainer"),
   *  resolved by the caller from session.identity + the server companion
   *  list. Always rendered — Falco wants the tag visible even when every
   *  session in view belongs to the same companion. */
  companionLabel: string;
}

const TYPE_ICONS = {
  personal: Heart,
  "film-studio": Clapperboard,
  "storyboard-studio": Clapperboard,
  "film-editing": Clapperboard,
  writing: BookOpen,
  building: Wrench,
  creative: Palette,
  build: Wrench,
  // Common Room is the lounge, not "Personal" — without its own entry this
  // fell through the `?? Heart` fallback below and read as a personal session.
  common_room: Sofa,
} as const;

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function SessionItem({
  session, active, onClick, onDelete, onRename, onTogglePin,
  onArchive, onRestore, archived = false, companionLabel,
  projects = [], onMoveToProject, projectMovePending = false,
  onWakeMeOnThis,
}: SessionItemProps) {
  const { t } = useI18n();
  const Icon = TYPE_ICONS[session.session_type as keyof typeof TYPE_ICONS] ?? Heart;
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Context menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [projectChooserOpen, setProjectChooserOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Long-press detection
  const longPressTimer = useRef<number | null>(null);
  // Flag to suppress the click after a long-press triggers
  const longPressFired = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Close menu on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;

    function handleOutsideClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  // --- Touch long-press ---
  function handleTouchStart(e: React.TouchEvent) {
    longPressFired.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      openMenu(e.touches[0].clientX, e.touches[0].clientY);
    }, 500);
  }

  function handleTouchEnd() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleTouchMove() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  // --- Right-click ---
  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    openMenu(e.clientX, e.clientY);
  }

  function openMenu(x: number, y: number) {
    const menuWidth = 260;
    const menuHeight = 420;
    setProjectChooserOpen(false);
    setMenuPos({
      x: Math.max(8, Math.min(x, window.innerWidth - menuWidth - 8)),
      y: Math.max(8, Math.min(y, window.innerHeight - menuHeight - 8)),
    });
    setMenuOpen(true);
  }

  // --- Row click — suppress if long-press just fired ---
  function handleRowClick() {
    if (editing || archived) return;
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    onClick();
  }

  function handleTogglePin(e: React.MouseEvent) {
    e.stopPropagation();
    onTogglePin();
  }

  function handleDoubleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (archived) return;
    setEditing(true);
  }

  function handleRenameSubmit() {
    const value = inputRef.current?.value.trim();
    if (value && value !== (session.label ?? "Untitled")) {
      onRename(value);
    }
    setEditing(false);
  }

  function handleRenameKeyDown(e: React.KeyboardEvent) {
    e.stopPropagation();
    if (e.key === "Enter") {
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      setEditing(false);
    }
  }

  // --- Context menu actions ---
  function menuRename() {
    setMenuOpen(false);
    setEditing(true);
  }

  function menuTogglePin() {
    setMenuOpen(false);
    onTogglePin();
  }

  function menuDelete() {
    setMenuOpen(false);
    if (window.confirm(t("sidebar.deleteConfirm"))) {
      onDelete();
    }
  }

  function menuArchive() {
    setMenuOpen(false);
    onArchive?.();
  }

  function menuRestore() {
    setMenuOpen(false);
    onRestore?.();
  }

  function menuWakeMeOnThis() {
    setMenuOpen(false);
    onWakeMeOnThis?.();
  }

  function menuMoveToProject(project: Project) {
    setMenuOpen(false);
    setProjectChooserOpen(false);
    onMoveToProject?.(project);
  }

  function menuRemoveFromProject() {
    setMenuOpen(false);
    setProjectChooserOpen(false);
    onMoveToProject?.(null);
  }

  function handleRestore(e: React.MouseEvent) {
    e.stopPropagation();
    onRestore?.();
  }

  const pinned = Boolean(session.pinned);
  const availableProjects = projects.filter((project) => (
    project.id !== session.project_id
  ));

  return (
    <>
      <div
        className={`session-item${active ? " active" : ""}${pinned ? " pinned" : ""}${archived ? " archived" : ""}`}
        onClick={handleRowClick}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        role={archived ? undefined : "button"}
        tabIndex={archived ? undefined : 0}
        onKeyDown={(e) => {
          if (!archived && !editing && (e.key === "Enter" || e.key === " ")) onClick();
          if (!archived && !editing && e.key === "F2") { e.preventDefault(); setEditing(true); }
        }}
      >
        <span className="session-item-icon">
          <Icon size={14} />
        </span>
        <div className="session-item-info">
          {editing ? (
            <input
              ref={inputRef}
              className="session-item-rename-input"
              defaultValue={session.label ?? ""}
              placeholder="Session name..."
              onBlur={handleRenameSubmit}
              onKeyDown={handleRenameKeyDown}
              onClick={(e) => e.stopPropagation()}
              maxLength={100}
            />
          ) : (
            <div className="session-item-label" onDoubleClick={handleDoubleClick}>
              {session.label ?? "Untitled"}
            </div>
          )}
          <div className="session-item-meta">
            <span className="session-item-companion-badge">{companionLabel}</span>
            {session.session_type === "common_room" && (
              <span className="session-item-companion-badge session-item-common-room-badge">
                {t("sidebar.commonRoomBadge")}
              </span>
            )}
            <span className="session-item-time">
              {relativeTime(session.last_activity)}
            </span>
          </div>
        </div>
        {archived ? (
          <button className="session-restore-btn" onClick={handleRestore} aria-label={`Restore ${session.label ?? 'Untitled'}`}>
            <ArchiveRestore size={13} />
            Restore
          </button>
        ) : (
          <button
            className={`session-pin-btn${pinned ? " active" : ""}`}
            onClick={handleTogglePin}
            aria-label={pinned ? "Unpin session" : "Pin session"}
          >
            <Pin size={13} />
          </button>
        )}
      </div>

      {menuOpen && menuPos && (
        <div
          ref={menuRef}
          className="session-context-menu"
          role="menu"
          style={{ top: menuPos.y, left: menuPos.x }}
        >
          {archived ? (
            <button className="session-context-item" role="menuitem" onClick={menuRestore}>
              <ArchiveRestore size={13} />
              Restore
            </button>
          ) : (
            <>
              <button className="session-context-item" role="menuitem" onClick={menuRename}>
                <Pencil size={13} />
                Rename
              </button>
              <button className="session-context-item" role="menuitem" onClick={menuTogglePin}>
                <Pin size={13} />
                {pinned ? "Unpin" : "Pin"}
              </button>
              {onWakeMeOnThis && (
                <button className="session-context-item" role="menuitem" onClick={menuWakeMeOnThis}>
                  <AlarmClock size={13} />
                  Wake me on this
                </button>
              )}
              {onMoveToProject && (
                <>
                  <button
                    className="session-context-item"
                    role="menuitem"
                    aria-haspopup="menu"
                    aria-expanded={projectChooserOpen}
                    aria-busy={projectMovePending}
                    disabled={projectMovePending}
                    onClick={() => setProjectChooserOpen((open) => !open)}
                  >
                    <FolderInput size={13} />
                    Move to Project
                    <ChevronRight size={13} className="session-context-chevron" />
                  </button>
                  {projectChooserOpen && (
                    <div className="session-project-chooser" role="menu" aria-label="Projects">
                      {availableProjects.length > 0 ? availableProjects.map((project) => (
                        <button
                          className="session-context-item"
                          role="menuitem"
                          key={project.id}
                          onClick={() => menuMoveToProject(project)}
                        >
                          <FolderInput size={13} />
                          <span>{project.name}</span>
                        </button>
                      )) : (
                        <div className="session-project-empty" role="status">No other projects.</div>
                      )}
                    </div>
                  )}
                  {session.project_id !== null && (
                    <button
                      className="session-context-item"
                      role="menuitem"
                      disabled={projectMovePending}
                      onClick={menuRemoveFromProject}
                    >
                      <FolderMinus size={13} />
                      Remove from Project
                    </button>
                  )}
                </>
              )}
              <button className="session-context-item" role="menuitem" onClick={menuArchive}>
                <Archive size={13} />
                Archive
              </button>
            </>
          )}
          <button className="session-context-item session-context-item--danger" role="menuitem" onClick={menuDelete}>
            <Trash2 size={13} />
            Delete
          </button>
        </div>
      )}
    </>
  );
}
